import React, { DragEvent, ReactElement, useContext, useRef, useState } from 'react';
import {
  DEPENDENT_DOCUMENT_VISIBILITY,
  DEPENDENT_DOCUMENT_VISIBILITY_GLOBAL_CASE,
  DOCUMENT_VISIBILITY,
  IChecklistItem,
  IDocumentMetadata,
} from 'src/shared/types/Documents';
import styles from './DocumentUploadArea.module.scss';
import { Toast } from '@ds';
import { IToastr } from '@ds/elements/Toast/Toast.types';
import classNames from 'classnames';
import { BeneDocAdditionalContext } from 'src/shared/providers/DocumentUploadContextProvider';
import { uploadDocument } from 'src/shared/services/document-api/Documents';
import { CountryCode } from 'src/utils/constants/common';
import { validateFilePropertiesBeforeUpload } from 'src/utils/FileUtils';
import DocumentsCenterBrowserModal from '../DocumentsCenterBrowserModal/DocumentsCenterBrowserModal';

interface IUploadAreaProps {
  checklistItem?: IChecklistItem;
  allowFromCobalt?: boolean;
  uploadPassportFile?: (fileList: File[]) => Promise<void> | void;
  onUploadComplete?: () => void;
}

const DocumentUploadArea = ({
  checklistItem,
  allowFromCobalt,
  uploadPassportFile,
  onUploadComplete,
}: IUploadAreaProps): ReactElement => {
  const toastrRef = useRef<IToastr>(null);
  const [isDocumentCenterBrowserModalOpen, setIsDocumentCenterBrowserModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { caseId, beneId, companyId, userId, otherDocTypeId, caseInfo } = useContext(BeneDocAdditionalContext);
  const documentType = checklistItem?.checklistDefinition?.documentTypeId || otherDocTypeId;

  const openDocumentCenterBrowserModal = () => {
    setIsDocumentCenterBrowserModalOpen(true);
  };

  const onFileDropped = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    validateAndProcessFiles([...e.dataTransfer.files]);
  };

  const onFileInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      validateAndProcessFiles([...e.target.files]);
    }
    e.target.value = '';
  };

  const validateAndProcessFiles = (files: File[]) => {
    const validationResult = validateFilePropertiesBeforeUpload(files);

    if (validationResult.isValid) {
      if (uploadPassportFile) {
        uploadPassportFile(files);
      } else {
        uploadFiles(files);
      }
    } else {
      toastrRef.current?.show({
        message: validationResult.errors[0],
        severity: 'error',
      });
    }
  };

  const buildFileName = (fileName: string): string => {
    const isBeneficiary = !!checklistItem?.beneficiary?.fullName;
    const name = isBeneficiary ? checklistItem?.beneficiary?.fullName : checklistItem?.dependent?.dependentFullName;

    const docType = checklistItem ? checklistItem?.checklistDefinition?.name : 'Other';

    return [fileName, name, docType].filter(Boolean).join(' - ');
  };

  const uploadFiles = async (fileList: File[]) => {
    const numFiles = fileList.length;
    let numUploaded = 0;

    for (const file of fileList) {
      toastrRef.current?.show({
        message: 'Uploading...',
        rightMessage: `${numFiles - numUploaded} ${numFiles - numUploaded > 1 ? 'Files' : 'File'} left`,
        severity: 'info',
        sticky: true,
      });
      const name = buildFileName(file.name);
      const metaData: IDocumentMetadata = {
        common: {
          beneId: beneId,
          clientCaseIds: [Number(caseId)],
          companyId: companyId,
          dependentId: checklistItem?.dependentId || null,
          visibility: checklistItem?.dependentId
            ? caseInfo?.caseCountryCode && caseInfo.caseCountryCode !== CountryCode.UnitedStates
              ? DEPENDENT_DOCUMENT_VISIBILITY_GLOBAL_CASE
              : DEPENDENT_DOCUMENT_VISIBILITY
            : DOCUMENT_VISIBILITY,
        },
        name: name,
        type: documentType ?? 0,
      };

      if (userId !== undefined) {
        try {
          if (caseId !== undefined) {
            await uploadDocument([file], metaData, undefined, name, caseId, userId, checklistItem?.id || undefined);
          } else {
            console.error('Case ID is null. Cannot upload document.');
          }
        } catch (error) {
          console.error('Error uploading document:', error);
        } finally {
          numUploaded++;
          toastrRef.current?.clear();
        }
      } else {
        console.error('User ID is undefined. Cannot upload document.');
      }
    }

    toastrRef.current?.clear();
    toastrRef.current?.show({
      message: 'Uploaded',
      rightMessage: `${numUploaded} ${numUploaded > 1 ? 'Files' : 'File'}`,
      severity: 'success',
      sticky: false,
    });

    onUploadComplete && onUploadComplete();
  };

  return (
    <div className={styles['upload-area-container']} onDragOver={(e) => e.preventDefault()} onDrop={onFileDropped}>
      <div className={styles['bene-upload-section']}>
        {allowFromCobalt && (
          <div className={classNames(styles['method-container'], styles['separator'])}>
            <div className={styles['icon-wrapper']}>
              <div className={classNames(styles['method-icon'], styles['folder-open-icon'])}></div>
            </div>
            <div className={styles['inner-content']}>
              <div className={styles['method-title']}>{'From Cobalt'}</div>
              <div className={styles['method-desc']}>
                Link documents you've previously uploaded via the&nbsp;
                <a
                  id="sb-add-documents-with-document-center-btn"
                  className={styles['redirect-link']}
                  onClick={openDocumentCenterBrowserModal}
                >
                  Cobalt Document Center
                </a>
              </div>
            </div>
          </div>
        )}
        <div className={styles['method-container']}>
          <div className={styles['icon-wrapper']}>
            <div className={classNames(styles['method-icon'], styles['upload-icon'])}></div>
          </div>
          <div className={styles['inner-content']}>
            <div className={styles['method-title']}>From Device</div>
            <div className={styles['method-desc']}>
              Drag and drop to upload or&nbsp;
              <a
                id="sb-browse-documents-from-computer-btn"
                className={styles['redirect-link']}
                onClick={() => {
                  fileInputRef.current && fileInputRef.current.click();
                }}
              >
                click to upload from your device
              </a>
              <input
                type="file"
                multiple={true}
                style={{ display: 'none' }}
                onChange={onFileInputChanged}
                ref={fileInputRef}
              />
            </div>
          </div>
        </div>
      </div>
      <div className={styles['agreement-text']}>
        By adding a document, you agree to share it with your immigration provider, network provider, and employer.
      </div>
      <Toast ref={toastrRef} />
      <DocumentsCenterBrowserModal
        checklistItem={checklistItem}
        isOpen={isDocumentCenterBrowserModalOpen}
        onClose={() => setIsDocumentCenterBrowserModalOpen(false)}
        onDocumentsAdded={onUploadComplete}
      />
    </div>
  );
};

export default DocumentUploadArea;
